import { LoadingMessage } from "components/shared/LoadingMessage";
import graphql from "babel-plugin-relay/macro";
import { IPaymentFormChildProps } from "components/payment/PaymentForm";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { Message } from "shared/components/Message";
import { paymentMessages } from "domain/payment/paymentMessages";
import { GlobalPaymentsButtonQuery } from "generatedQueries/GlobalPaymentsButtonQuery.graphql";
import { GlobalPaymentsButton_configuration } from "generatedQueries/GlobalPaymentsButton_configuration.graphql";
import React, { FC, ReactNode, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { createFragmentContainer } from "react-relay";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { Query } from "shared/components/Query";
import { sharedMessages } from "shared/sharedMessages";
import { Money } from "components/shared/Money";
import { BasePaymentButton } from "components/payment/BasePaymentButton";
import { mapMessages } from "shared/mapMessages";

export const messages = mapMessages("components.payment.globalPayments.globalPaymentsButton", {
    paymentFailureTitle: "Failed to process payment with Global Payments",
    paymentFailureMessage: "{requestFailedMessage}{globalPaymentsErrorMessage}",
    payWithGlobalPaymentsMessage: "Pay {amount} with Global Payments",
});

interface IGraphQlProps {
    configuration: GlobalPaymentsButton_configuration;
}

interface IProps {
    allPaymentRowsAreValid: boolean;
    children: (authoriseRequestJson: string, onPaymentSuccess: () => void, onPaymentError: (errorMessage?: string) => void) => ReactNode;
    termsAndConditionsAccepted: boolean;
}

type Props = Pick<IPaymentFormChildProps, "amountToCharge" | "errorMessages" | "isPaymentProcessing"> & IGraphQlProps & IProps;

const GlobalPaymentsButtonInternal: FC<Props> = ({
    amountToCharge,
    allPaymentRowsAreValid,
    children,
    configuration,
    errorMessages,
    isPaymentProcessing,
    termsAndConditionsAccepted,
}) => {
    const intl = useIntl();
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentError, setPaymentError] = useState<{ hasError: true; errorMessage?: string;} | null>(null);

    const onPaymentSuccess = useCallback(() => {
        setIsPaymentModalOpen(false);
        setPaymentError(null);
    }, []);

    const onPaymentError = useCallback((errorMessage?: string) => {
        setIsPaymentModalOpen(false);
        setPaymentError({ hasError: true, errorMessage });
    }, []);

    const isPaymentFormValid = amountToCharge > 0 && termsAndConditionsAccepted && allPaymentRowsAreValid;
    const isPaymentButtonDisabled = !isPaymentFormValid || isPaymentProcessing;

    const disabledReason = isPaymentButtonDisabled && !isPaymentProcessing
        ? intl.formatMessage(paymentMessages.paymentButtonDisabledWarning)
        : undefined;

    const payWithGlobalPaymentsMessage =
        <Message
            message={messages.payWithGlobalPaymentsMessage}
            values={{ amount: <Money amount={amountToCharge} currency={configuration.currency} /> }} />;

    return <>
        {errorMessages}
        {paymentError?.hasError && <div className="mt-3">
            <ErrorMessage
                heading={messages.paymentFailureTitle}
                message={messages.paymentFailureMessage}
                messageValues={{
                    globalPaymentsErrorMessage: !!paymentError.errorMessage ? <div><code>{paymentError.errorMessage}</code></div> : undefined,
                    requestFailedMessage: <Message message={sharedMessages.requestFailedBody} />
                }} />
        </div>}
        <div className="text-center mb-3">
            <BasePaymentButton
                amount={amountToCharge}
                currency={configuration.currency}
                isDisabled={isPaymentButtonDisabled}
                isProcessing={isPaymentProcessing}
                onClick={onClick}
                className="w-75"
                data-cy="payButton"
                data-testid="payButton"
                disabledReason={disabledReason}
                size="lg"
            />
        </div>
        <Modal labelledBy="global-payments-modal-title" isOpen={isPaymentModalOpen} toggle={toggleModal} backdrop="static">
            <ModalHeader id="global-payments-modal-title" tag="h3" className="h5" toggle={toggleModal}>{payWithGlobalPaymentsMessage}</ModalHeader>
            <ModalBody>
                {isPaymentModalOpen &&
                    <Query<GlobalPaymentsButtonQuery>
                        cacheConfig={{ force: true }} /* Force create new global payments transaction as you cannot reuse same transaction after form submission */
                        fetchPolicy="network-only"
                        query={query}
                        variables={{ amount: amountToCharge }}>
                        {({ error, props }) => {
        if (error) {
            return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
        }

        if (!props) {
            return <LoadingMessage />;
        }

        if (!props.globalPaymentsHppPaymentRequestJson) {
            return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
        }

                            return children(props.globalPaymentsHppPaymentRequestJson, onPaymentSuccess, onPaymentError);
                        }}
                    </Query>
                }
            </ModalBody>
        </Modal>
    </>;

    function onClick() {
        if (!isPaymentFormValid || isPaymentProcessing) {
            return;
        }
        setIsPaymentModalOpen(true);
    }

    function toggleModal() {
        setIsPaymentModalOpen(!isPaymentModalOpen);
    }
};

export const GlobalPaymentsButton = createFragmentContainer(GlobalPaymentsButtonInternal, {
    configuration: graphql`
        fragment GlobalPaymentsButton_configuration on Configuration {
            currency
            organisationContact
        }`,
});

const query = graphql`
    query GlobalPaymentsButtonQuery($amount: Decimal!) {
        globalPaymentsHppPaymentRequestJson(amount: $amount)
    }`;
